import React, { useEffect, useState } from "react";
import Button from "@amzn/meridian/button";
import Box from "@amzn/meridian/box";
import { Space } from "../../space/Space";
import { Storage } from "aws-amplify";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { StatusEnum } from "../../../endpoints/getArtifacts";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import { useAppSelector } from "../../../app/store/hooks";
import { v4 as uuidv4 } from "uuid";
import LoaderBox from "../../loader/LoaderBox";

interface DownloadPackageProps {
    onPrevious: () => void;
    data: any;
    pdfBlob: Blob;
}

export const DownloadPackage: React.FC<DownloadPackageProps> = ({ onPrevious, data, pdfBlob }) => {
    const navigate = useNavigate();
    const { propertyId } = useParams();
    const uuid = uuidv4();
    const [updateAppeal] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(url);

            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [pdfBlob]);

    const handleRedirect = async () => {
        try {
            setIsLoading(true);
            await Storage.put(
                `APPRAISER-AI/APPEALS/EXPORT-PDF/${data?.appealArtifactId}/${uuid}.pdf`,
                pdfBlob,
                {
                    contentType: "application/pdf",
                    useAccelerateEndpoint: true,
                }
            );
        } catch (error) {
            console.error("Error updating appeal status:", error);
        } finally {
            const updatedData = JSON.parse(JSON.stringify(data));
            updatedData.appealDetails.appealApproachCalculationData.exportAppealsPackage.appealsPackagePdfMetadata =
                {
                    createdBy: alias,
                    canopyS3Key: `APPRAISER-AI/APPEALS/EXPORT-PDF/${data?.appealArtifactId}/${uuid}.pdf`,
                };

            await updateAppeal({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                appealDetails: updatedData.appealDetails,
                appealStatus: StatusEnum.UNDER_APPEAL,
                clientName: alias,
            });
            setIsLoading(false);
            // Navigate back to property page
            navigate(`/property-details/${propertyId}`);
        }
    };

    const handleDownload = () => {
        // Create a temporary link element
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `appeal-${uuid}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (isLoading) {
        return <LoaderBox loadingText="Saving PDF..." />;
    }

    return (
        <Box>
            <Space size={"l"} />
            <Row alignmentHorizontal={"justify"}>
                <Text type={"h500"}>Download Package</Text>
                <Row alignmentHorizontal={"end"} spacingInset={"200"}>
                    <Button size={"large"} onClick={onPrevious} type={"secondary"}>
                        Back
                    </Button>
                    <Button type={"primary"} size={"large"} onClick={handleRedirect}>
                        Done
                    </Button>
                    <Button type={"tertiary"} size={"large"} onClick={handleDownload}>
                        Download
                    </Button>
                </Row>
            </Row>
            <Box minHeight={"100vh"}>
                {pdfUrl && (
                    <iframe
                        src={pdfUrl}
                        style={{ width: "100%", height: "100vh", border: "none" }}
                        title="PDF Preview"
                    />
                )}
            </Box>
        </Box>
    );
};
